import { useState, useEffect, useRef, useCallback } from 'react';
import {
  AlertTriangle,
  Check,
  Copy,
  Hash,
  Link2,
  LogOut,
  MessageSquare,
  MoreVertical,
  RefreshCw,
  Save,
  Search,
  Shield,
  Trash2,
  Upload,
  UserMinus,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  api,
  ChatChannelResponse,
  ChatChannelMemberResponse,
  ChatChannelBanResponse,
  ChatDmPartnerProfile,
  ChatMemberPermissions,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { seededGradient } from '../utils';

// ---------------------------------------------------------------------------
// Shared primitives (mirrors ChatSettingsModal layout)
// ---------------------------------------------------------------------------

function SectionHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-6">
      <h2 className="text-base font-semibold">{title}</h2>
      {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
    </div>
  );
}

function SettingRow({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0">
      <div className="flex flex-col gap-0.5 mr-4">
        <span className="text-sm font-medium">{label}</span>
        {description && <span className="text-xs text-muted-foreground">{description}</span>}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Image upload helpers (mirrors ChatSettingsModal pattern)
// ---------------------------------------------------------------------------

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

async function uploadAvatarFile(file: File): Promise<{ fileId: string; previewUrl: string }> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Please upload a JPEG, PNG, WebP, or GIF image.');
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error('Image must be smaller than 8 MB.');
  }

  const presigned = await api.getUploadUrl({
    fileName: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
    category: 'media',
  });

  const uploadRes = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!uploadRes.ok) throw new Error('Upload failed. Please try again.');

  const { file: confirmed } = await api.confirmUpload(presigned.fileId, {
    fileName: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
    s3Key: presigned.s3Key,
    category: 'media',
  });

  const { downloadUrl } = await api.getDownloadUrl(confirmed.fileId);
  return { fileId: confirmed.fileId, previewUrl: downloadUrl };
}

function AvatarUploader({
  displayName,
  previewUrl,
  onChange,
}: {
  displayName: string;
  fileId: string | null;
  previewUrl: string | null;
  onChange: (fileId: string | null, previewUrl: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setUploading(true);
      try {
        const result = await uploadAvatarFile(file);
        onChange(result.fileId, result.previewUrl);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed.');
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [onChange],
  );

  const avatarSrc = previewUrl;

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm">Group Avatar</Label>
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 rounded-full overflow-hidden flex-shrink-0 border border-border">
          {avatarSrc ? (
            <img src={avatarSrc} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-white text-2xl font-bold"
              style={{ background: seededGradient(displayName) }}
            >
              #
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="h-8 text-xs gap-1.5"
          >
            <Upload className="w-3.5 h-3.5" />
            {avatarSrc ? 'Replace' : 'Upload'}
          </Button>
          {avatarSrc && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(null, null)}
              className="h-8 text-xs gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove
            </Button>
          )}
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

type TabName = 'Info' | 'Members' | 'Permissions' | 'Privacy' | 'Danger';

interface TabDef {
  icon: React.ReactNode;
  name: TabName;
}

function getTabs(channelType: string, isAdmin: boolean): TabDef[] {
  if (channelType === 'direct') {
    return [
      { icon: <MessageSquare className="w-4 h-4" />, name: 'Info' },
      { icon: <Shield className="w-4 h-4" />, name: 'Privacy' },
    ];
  }

  const tabs: TabDef[] = [
    { icon: <Hash className="w-4 h-4" />, name: 'Info' },
    { icon: <Users className="w-4 h-4" />, name: 'Members' },
  ];

  if (isAdmin) {
    tabs.push({ icon: <Shield className="w-4 h-4" />, name: 'Permissions' });
  }

  tabs.push({ icon: <AlertTriangle className="w-4 h-4" />, name: 'Danger' });
  return tabs;
}

function TabItem({
  tab,
  active,
  onClick,
  mobile = false,
}: {
  tab: TabDef;
  active: boolean;
  onClick: () => void;
  mobile?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 transition-colors rounded-lg text-sm',
        mobile ? 'px-3 py-2 whitespace-nowrap flex-shrink-0' : 'px-3 py-2 w-full text-left',
        active
          ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      <span className="flex-shrink-0">{tab.icon}</span>
      <span>{tab.name}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Role badge
// ---------------------------------------------------------------------------

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-yellow-500/15 text-yellow-600 border-yellow-400/30',
  admin: 'bg-blue-500/15 text-blue-600 border-blue-400/30',
  moderator: 'bg-green-500/15 text-green-600 border-green-400/30',
  member: 'bg-muted text-muted-foreground border-border',
};

function RoleBadge({ role }: { role: string }) {
  return (
    <span
      className={cn(
        'text-[10px] font-medium px-1.5 py-0.5 rounded border capitalize',
        ROLE_COLORS[role] ?? ROLE_COLORS.member,
      )}
    >
      {role}
    </span>
  );
}

// ---------------------------------------------------------------------------
// DM partner profile card (reuses layout from ChatSettingsModal ProfileCard)
// ---------------------------------------------------------------------------

function DmProfileCard({ profile }: { profile: ChatDmPartnerProfile }) {
  const name = profile.displayName || profile.username;
  const initial = name.charAt(0).toUpperCase();

  return (
    <div className="relative rounded-[18px] bg-[#1c1c1e] border border-white/10 overflow-hidden max-w-[360px] shadow-xl">
      <div
        className="h-[100px] w-full transition-colors duration-300"
        style={{ backgroundColor: profile.bannerColor }}
      />
      <div className="relative px-4 pb-4">
        <div
          className="absolute -top-9 left-4 w-[72px] h-[72px] rounded-full border-4 border-[#1c1c1e] overflow-hidden flex items-center justify-center text-white text-2xl font-bold select-none"
          style={{ backgroundColor: profile.avatarColor }}
        >
          {initial}
        </div>
        <div className="pt-11">
          <p className="text-[20px] font-bold text-white leading-tight">{name}</p>
          <p className="text-sm text-[#a1a1aa] mt-0.5">@{profile.username}</p>
          {profile.bio && (
            <p className="text-sm text-[#d4d4d8] mt-2 leading-relaxed break-words">{profile.bio}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Permission toggles
// ---------------------------------------------------------------------------

const PERMISSION_ROWS: Array<{
  key: keyof ChatMemberPermissions;
  label: string;
  description: string;
}> = [
  { key: 'sendMessages', label: 'Send Messages', description: 'Post text messages' },
  { key: 'sendImages', label: 'Send Images', description: 'Share image files' },
  { key: 'sendVideos', label: 'Send Videos', description: 'Share video files' },
  { key: 'sendAudio', label: 'Send Audio', description: 'Share audio files' },
  { key: 'sendFiles', label: 'Send Files', description: 'Share PDFs and other documents' },
  { key: 'sendLinks', label: 'Send Links', description: 'Share URLs' },
  {
    key: 'sendStickers',
    label: 'Send Stickers & GIFs',
    description: 'Share stickers and animated GIFs',
  },
  { key: 'addUsers', label: 'Add Users', description: 'Invite new members to this channel' },
  { key: 'pinMessages', label: 'Pin Messages', description: 'Pin and unpin messages' },
];

const ADMIN_PERMISSION_ROWS: Array<{
  key: keyof ChatMemberPermissions;
  label: string;
  description: string;
}> = [
  {
    key: 'changeGroupInfo',
    label: 'Change Group Info',
    description: 'Edit name, description, and avatar',
  },
  { key: 'banUsers', label: 'Ban & Remove Users', description: 'Remove and ban members' },
  { key: 'deleteMessages', label: 'Delete Messages', description: "Delete any member's messages" },
  { key: 'addAdmins', label: 'Add Admins', description: 'Promote members to admin' },
];

function PermissionToggles({
  title,
  description,
  rows,
  permissions,
  onChange,
}: {
  title: string;
  description: string;
  rows: typeof PERMISSION_ROWS;
  permissions: ChatMemberPermissions;
  onChange: (updated: ChatMemberPermissions) => void;
}) {
  return (
    <div className="space-y-2">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <div className="space-y-0 divide-y divide-border/50">
        {rows.map(({ key, label, description: desc }) => (
          <SettingRow key={key} label={label} description={desc}>
            <Switch
              checked={permissions[key] !== false}
              onCheckedChange={(v) => onChange({ ...permissions, [key]: v })}
            />
          </SettingRow>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add member search
// ---------------------------------------------------------------------------

function AddMemberSearch({
  channelId,
  memberIds,
  onAdded,
}: {
  channelId: string;
  memberIds: Set<string>;
  onAdded: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<
    Array<{ userId: string; username: string; displayName: string; isMember: boolean }>
  >([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleQueryChange(value: string) {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.searchChatUsers(channelId, value.trim());
        setResults(r.users);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  }

  async function handleAdd(userId: string) {
    setAdding(userId);
    try {
      await api.addChatMember(channelId, userId);
      setAddedIds((prev) => new Set([...prev, userId]));
      onAdded();
    } catch {
      // leave results intact so user can retry
    } finally {
      setAdding(null);
    }
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          placeholder="Search by username…"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          className="pl-9 text-sm h-9"
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
        )}
      </div>
      {results.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden divide-y divide-border/50">
          {results.map((user) => {
            const alreadyMember = memberIds.has(user.userId) || addedIds.has(user.userId);
            return (
              <div key={user.userId} className="flex items-center gap-3 px-3 py-2.5">
                <MemberAvatar displayName={user.displayName} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user.displayName}</p>
                  <p className="text-xs text-muted-foreground">@{user.username}</p>
                </div>
                {alreadyMember ? (
                  <span className="text-xs text-muted-foreground flex-shrink-0">Already added</span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAdd(user.userId)}
                    disabled={adding === user.userId}
                    className="h-7 text-xs flex-shrink-0 gap-1"
                  >
                    <UserPlus className="w-3 h-3" />
                    {adding === user.userId ? 'Adding…' : 'Add'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {query.trim() && !searching && results.length === 0 && (
        <p className="text-xs text-muted-foreground px-1">No user found for &quot;{query}&quot;</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin row — shows an admin's permission scope at a glance
// ---------------------------------------------------------------------------

function MemberAvatar({
  displayName,
  avatarUrl,
  size = 'md',
}: {
  displayName: string;
  avatarUrl?: string | null;
  size?: 'sm' | 'md';
}) {
  const cls = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-9 h-9 text-sm';
  return (
    <div
      className={cn(
        cls,
        'rounded-full flex-shrink-0 flex items-center justify-center font-semibold text-white overflow-hidden',
      )}
      style={avatarUrl ? undefined : { background: seededGradient(displayName) }}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
      ) : (
        displayName.charAt(0).toUpperCase()
      )}
    </div>
  );
}

function AdminRow({
  member,
  displayName,
  avatarUrl,
  currentUserId,
  currentUserRole,
  onPermissionEdit,
}: {
  member: ChatChannelMemberResponse;
  displayName: string;
  avatarUrl?: string | null;
  currentUserId: string | null | undefined;
  currentUserRole: string;
  onPermissionEdit: (member: ChatChannelMemberResponse) => void;
}) {
  const isSelf = member.userId === currentUserId;
  const canEdit = currentUserRole === 'owner' && !isSelf;

  const grantedAdminPerms = ADMIN_PERMISSION_ROWS.filter(
    (row) => member.permissions?.[row.key] !== false,
  );

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0">
      <MemberAvatar displayName={displayName} avatarUrl={avatarUrl} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className="text-sm font-medium truncate">{displayName}</span>
          {isSelf && <span className="text-[10px] text-muted-foreground">(you)</span>}
          <RoleBadge role={member.role} />
        </div>
        <div className="flex flex-wrap gap-1">
          {grantedAdminPerms.length > 0 ? (
            grantedAdminPerms.map((perm) => (
              <span
                key={perm.key}
                className="text-[10px] bg-blue-500/10 text-blue-600 border border-blue-400/20 rounded px-1.5 py-0.5"
              >
                {perm.label}
              </span>
            ))
          ) : (
            <span className="text-[10px] text-muted-foreground">No admin permissions granted</span>
          )}
        </div>
      </div>
      {canEdit && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 flex-shrink-0"
          title="Edit permissions"
          onClick={() => onPermissionEdit(member)}
        >
          <Shield className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Member row with role management
// ---------------------------------------------------------------------------

function MemberRow({
  member,
  displayName,
  avatarUrl,
  currentUserId,
  currentUserRole,
  onRoleChange,
  onRemove,
  onBan,
  onPermissionEdit,
}: {
  member: ChatChannelMemberResponse;
  displayName: string;
  avatarUrl?: string | null;
  currentUserId: string | null | undefined;
  currentUserRole: string;
  channelId: string;
  onRoleChange: (userId: string, role: string) => void;
  onRemove: (userId: string) => void;
  onBan: (userId: string) => void;
  onPermissionEdit: (member: ChatChannelMemberResponse) => void;
}) {
  const isSelf = member.userId === currentUserId;
  const canManage =
    !isSelf &&
    (currentUserRole === 'owner' ||
      (currentUserRole === 'admin' && member.role !== 'owner' && member.role !== 'admin'));

  return (
    <div className="flex items-center gap-3 py-2.5">
      <MemberAvatar displayName={displayName} avatarUrl={avatarUrl} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{displayName}</span>
          {isSelf && <span className="text-[10px] text-muted-foreground">(you)</span>}
          <RoleBadge role={member.role} />
        </div>
      </div>
      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onPermissionEdit(member)}>
              <Shield className="w-4 h-4 mr-2" />
              Edit permissions
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {currentUserRole === 'owner' && member.role !== 'admin' && (
              <DropdownMenuItem onClick={() => onRoleChange(member.userId, 'admin')}>
                Promote to admin
              </DropdownMenuItem>
            )}
            {currentUserRole === 'owner' && member.role === 'admin' && (
              <DropdownMenuItem onClick={() => onRoleChange(member.userId, 'member')}>
                Remove admin
              </DropdownMenuItem>
            )}
            {member.role === 'member' && (
              <DropdownMenuItem onClick={() => onRoleChange(member.userId, 'moderator')}>
                Promote to moderator
              </DropdownMenuItem>
            )}
            {member.role === 'moderator' && (
              <DropdownMenuItem onClick={() => onRoleChange(member.userId, 'member')}>
                Demote to member
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onRemove(member.userId)}
              className="text-destructive focus:text-destructive"
            >
              <UserMinus className="w-4 h-4 mr-2" />
              Remove from group
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onBan(member.userId)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Ban user
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

interface ChannelSettingsModalProps {
  channel: ChatChannelResponse;
  members: ChatChannelMemberResponse[];
  currentUserId: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLeft?: () => void;
  onDeleted?: () => void;
}

export function ChannelSettingsModal({
  channel,
  members,
  currentUserId,
  open,
  onOpenChange,
  onLeft,
  onDeleted,
}: ChannelSettingsModalProps) {
  const queryClient = useQueryClient();
  const isDirect = channel.channelType === 'direct';

  const currentMember = members.find((m) => m.userId === currentUserId);
  const isAdmin = currentMember?.role === 'owner' || currentMember?.role === 'admin';
  const isOwner = currentMember?.role === 'owner';

  const tabs = getTabs(channel.channelType, isAdmin);
  const defaultTab = tabs[0].name;
  const [activeTab, setActiveTab] = useState<TabName>(defaultTab);

  // ----- Info state (group) -----
  const [name, setName] = useState(channel.name ?? '');
  const [description, setDescription] = useState(channel.description ?? '');
  const [avatarFileId, setAvatarFileId] = useState<string | null>(channel.avatarFileId ?? null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [isPrivate, setIsPrivate] = useState(channel.isPrivate ?? false);
  const [saving, setSaving] = useState(false);

  const hasInfoChanges =
    name !== (channel.name ?? '') ||
    description !== (channel.description ?? '') ||
    avatarFileId !== (channel.avatarFileId ?? null) ||
    isPrivate !== (channel.isPrivate ?? false);

  // ----- Invite code state -----
  const [inviteCode, setInviteCode] = useState(channel.inviteCode ?? null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteGenerating, setInviteGenerating] = useState(false);

  // ----- DM partner profile -----
  const [partnerProfile, setPartnerProfile] = useState<ChatDmPartnerProfile | null>(null);

  // ----- Members state -----
  const [memberAvatarUrls, setMemberAvatarUrls] = useState<Record<string, string>>({});

  // ----- Permissions state -----
  const [defaultPermissions, setDefaultPermissions] = useState<ChatMemberPermissions>(
    channel.defaultPermissions ?? {},
  );
  const [permSaving, setPermSaving] = useState(false);
  const hasPermChanges =
    JSON.stringify(defaultPermissions) !== JSON.stringify(channel.defaultPermissions ?? {});

  // ----- Per-member permission editing -----
  const [editingMember, setEditingMember] = useState<ChatChannelMemberResponse | null>(null);
  const [memberPermissions, setMemberPermissions] = useState<ChatMemberPermissions>({});
  const [memberPermSaving, setMemberPermSaving] = useState(false);

  // ----- Banned users -----
  const [bans, setBans] = useState<ChatChannelBanResponse[]>([]);
  const [bansLoaded, setBansLoaded] = useState(false);

  // ----- Notifications -----
  const [isMuted, setIsMuted] = useState(currentMember?.isMuted ?? false);
  const [muteLoading, setMuteLoading] = useState(false);

  // ----- Danger -----
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [showDmDeleteConfirm, setShowDmDeleteConfirm] = useState(false);

  type DangerMode = null | 'transfer-leave' | 'delete-confirm';
  const [dangerMode, setDangerMode] = useState<DangerMode>(null);
  const [selectedNewOwnerId, setSelectedNewOwnerId] = useState<string | null>(null);
  const [transferLeaveLoading, setTransferLeaveLoading] = useState(false);

  // Reset state when modal opens / channel changes
  useEffect(() => {
    if (!open) return;

    setActiveTab(defaultTab);
    setName(channel.name ?? '');
    setDescription(channel.description ?? '');
    setAvatarFileId(channel.avatarFileId ?? null);
    setAvatarPreviewUrl(null);
    setIsPrivate(channel.isPrivate ?? false);
    setInviteCode(channel.inviteCode ?? null);
    setIsMuted(currentMember?.isMuted ?? false);
    setDefaultPermissions(channel.defaultPermissions ?? {});
    setEditingMember(null);
    setDangerMode(null);
    setSelectedNewOwnerId(null);
    setShowDmDeleteConfirm(false);
    setBansLoaded(false);
    setBans([]);

    if (isDirect) {
      api
        .getChatDmPartnerProfile(channel.channelId)
        .then((r) => setPartnerProfile(r.profile))
        .catch(() => {});
    }
  }, [open, channel.channelId]);

  // Resolve avatar preview URL for existing avatarFileId
  useEffect(() => {
    if (avatarFileId && !avatarPreviewUrl) {
      api
        .getDownloadUrl(avatarFileId)
        .then((r) => setAvatarPreviewUrl(r.downloadUrl))
        .catch(() => {});
    }
  }, [avatarFileId]);

  // Resolve avatar download URLs for members that have an avatarImageId
  useEffect(() => {
    if (!open) return;
    const membersWithAvatar = members.filter((m) => m.avatarImageId);
    if (membersWithAvatar.length === 0) {
      setMemberAvatarUrls({});
      return;
    }
    let cancelled = false;
    Promise.all(
      membersWithAvatar.map((m) =>
        api
          .getDownloadUrl(m.avatarImageId!)
          .then((r) => ({ userId: m.userId, url: r.downloadUrl }))
          .catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      const urls: Record<string, string> = {};
      for (const r of results) {
        if (r) urls[r.userId] = r.url;
      }
      setMemberAvatarUrls(urls);
    });
    return () => {
      cancelled = true;
    };
  }, [open, members]);

  // Load bans when Permissions or Members tab opens (admin only)
  useEffect(() => {
    if (!bansLoaded && isAdmin && (activeTab === 'Members' || activeTab === 'Permissions')) {
      setBansLoaded(true);
      api
        .getChatChannelBans(channel.channelId)
        .then((r) => setBans(r.bans))
        .catch(() => {});
    }
  }, [activeTab, isAdmin, bansLoaded, channel.channelId]);

  function invalidateChannel() {
    queryClient.invalidateQueries({ queryKey: queryKeys.chat.channel(channel.channelId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.chat.channels() });
  }

  // --- Info save ---
  async function handleInfoSave() {
    setSaving(true);
    try {
      await api.updateChatChannel(channel.channelId, {
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        imageRef: avatarFileId ? (avatarPreviewUrl ?? undefined) : null,
        avatarFileId,
        isPrivate,
      });
      invalidateChannel();
    } finally {
      setSaving(false);
    }
  }

  function handleInfoReset() {
    setName(channel.name ?? '');
    setDescription(channel.description ?? '');
    setAvatarFileId(channel.avatarFileId ?? null);
    setAvatarPreviewUrl(null);
    setIsPrivate(channel.isPrivate ?? false);
  }

  // --- Invite code ---
  async function handleGenerateInvite() {
    setInviteGenerating(true);
    try {
      const result = await api.generateChatChannelInvite(channel.channelId);
      setInviteCode(result.inviteCode);
      invalidateChannel();
    } finally {
      setInviteGenerating(false);
    }
  }

  function handleCopyInvite() {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode).then(() => {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    });
  }

  // --- Mute ---
  async function handleToggleMute(value: boolean) {
    setMuteLoading(true);
    try {
      if (value) {
        await api.muteChatChannel(channel.channelId);
      } else {
        await api.unmuteChatChannel(channel.channelId);
      }
      setIsMuted(value);
      queryClient.invalidateQueries({ queryKey: queryKeys.chat.channel(channel.channelId) });
    } finally {
      setMuteLoading(false);
    }
  }

  // --- Permissions save ---
  async function handlePermSave() {
    setPermSaving(true);
    try {
      await api.updateChatChannel(channel.channelId, { defaultPermissions });
      invalidateChannel();
    } finally {
      setPermSaving(false);
    }
  }

  // --- Per-member permissions ---
  function openMemberPermEdit(member: ChatChannelMemberResponse) {
    setEditingMember(member);
    setMemberPermissions(member.permissions ?? {});
  }

  async function handleMemberPermSave() {
    if (!editingMember) return;
    setMemberPermSaving(true);
    try {
      await api.updateChatMemberPermissions(
        channel.channelId,
        editingMember.userId,
        memberPermissions,
      );
      invalidateChannel();
      setEditingMember(null);
    } finally {
      setMemberPermSaving(false);
    }
  }

  // --- Role change ---
  async function handleRoleChange(userId: string, role: string) {
    try {
      await api.updateChatMemberRole(channel.channelId, userId, role);
      invalidateChannel();
    } catch {
      // silently fail; user sees stale data until next refresh
    }
  }

  // --- Remove member ---
  async function handleRemoveMember(userId: string) {
    try {
      await api.removeChatMember(channel.channelId, userId);
      invalidateChannel();
    } catch {
      //
    }
  }

  // --- Ban member ---
  async function handleBanMember(userId: string) {
    try {
      const result = await api.banChatMember(channel.channelId, userId);
      setBans((prev) => [...prev, result.ban]);
      invalidateChannel();
    } catch {
      //
    }
  }

  // --- Unban ---
  async function handleUnban(userId: string) {
    try {
      await api.unbanChatMember(channel.channelId, userId);
      setBans((prev) => prev.filter((b) => b.bannedUserId !== userId));
    } catch {
      //
    }
  }

  // --- Block DM partner ---
  async function handleBlockPartner() {
    if (!partnerProfile) return;
    setBlockLoading(true);
    try {
      await api.blockChatUser(partnerProfile.userId);
    } finally {
      setBlockLoading(false);
    }
  }

  // --- Leave ---
  async function handleLeave() {
    setLeaveLoading(true);
    try {
      await api.leaveChatChannel(channel.channelId);
      invalidateChannel();
      onOpenChange(false);
      onLeft?.();
    } finally {
      setLeaveLoading(false);
    }
  }

  async function handleTransferAndLeave() {
    if (!selectedNewOwnerId) return;
    setTransferLeaveLoading(true);
    try {
      await api.updateChatMemberRole(channel.channelId, selectedNewOwnerId, 'owner');
      await api.leaveChatChannel(channel.channelId);
      invalidateChannel();
      onOpenChange(false);
      onLeft?.();
    } finally {
      setTransferLeaveLoading(false);
    }
  }

  // --- Rejoin ---
  async function handleRejoin() {
    setLeaveLoading(true);
    try {
      await api.joinChatChannel(channel.channelId);
      invalidateChannel();
      onOpenChange(false);
    } finally {
      setLeaveLoading(false);
    }
  }

  // --- Delete ---
  async function handleDelete() {
    setDeleteLoading(true);
    try {
      await api.deleteChatChannel(channel.channelId);
      invalidateChannel();
      onOpenChange(false);
      onDeleted?.();
    } finally {
      setDeleteLoading(false);
    }
  }

  const channelDisplayName = isDirect
    ? partnerProfile?.displayName ||
      partnerProfile?.username ||
      channel.dmPartnerDisplayName ||
      'Direct Message'
    : channel.name || 'Unnamed Channel';

  // ---------------------------------------------------------------------------
  // Render content for the active tab
  // ---------------------------------------------------------------------------

  function renderContent() {
    // Per-member permission editor (overlay)
    if (editingMember) {
      return (
        <div className="space-y-6">
          <div className="flex flex-row-reverse items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setEditingMember(null)}
              className="text-sm align-left w-fit text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back
            </button>
            <div>
              <h2 className="text-base font-semibold">Member Permissions</h2>
              <p className="text-xs text-muted-foreground">
                Overrides for{' '}
                <span className="font-medium">
                  {editingMember.displayName ?? editingMember.userId}
                </span>
              </p>
            </div>
          </div>

          {currentMember?.role === 'owner' && (
            <PermissionToggles
              title="Admin Actions"
              description="Admin-level capabilities for this member."
              rows={ADMIN_PERMISSION_ROWS}
              permissions={memberPermissions}
              onChange={setMemberPermissions}
            />
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button
              size="sm"
              onClick={handleMemberPermSave}
              disabled={memberPermSaving}
              className="bg-foreground text-background hover:bg-foreground/80 gap-1.5"
            >
              <Save className="w-3.5 h-3.5" />
              Save
            </Button>
            <button
              type="button"
              onClick={() => setEditingMember(null)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'Info': {
        if (isDirect && partnerProfile) {
          return (
            <div className="space-y-6">
              <SectionHeading title="Info" description="About this conversation." />
              <DmProfileCard profile={partnerProfile} />
              <div className="space-y-0 divide-y divide-border/50">
                <SettingRow
                  label="Mute Conversation"
                  description="Suppress notifications from this DM"
                >
                  <Switch
                    checked={isMuted}
                    onCheckedChange={handleToggleMute}
                    disabled={muteLoading}
                  />
                </SettingRow>
              </div>
            </div>
          );
        }

        if (isDirect) {
          return (
            <div className="space-y-6">
              <SectionHeading title="Info" description="Direct message conversation." />
              <div className="space-y-0 divide-y divide-border/50">
                <SettingRow label="Mute Conversation" description="Suppress notifications">
                  <Switch
                    checked={isMuted}
                    onCheckedChange={handleToggleMute}
                    disabled={muteLoading}
                  />
                </SettingRow>
              </div>
            </div>
          );
        }

        // Group/Channel info
        return (
          <div className="space-y-6">
            <SectionHeading
              title="Info"
              description={isAdmin ? 'Edit your group chat details.' : 'Group information.'}
            />

            {/* Avatar */}
            {isAdmin ? (
              <AvatarUploader
                displayName={channelDisplayName}
                fileId={avatarFileId}
                previewUrl={avatarPreviewUrl}
                onChange={(fid, preview) => {
                  setAvatarFileId(fid);
                  setAvatarPreviewUrl(preview);
                }}
              />
            ) : (
              <div className="flex items-center gap-3">
                <div
                  className="w-14 h-14 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-white text-xl font-bold border border-border"
                  style={
                    !avatarPreviewUrl && !channel.imageRef
                      ? { background: seededGradient(channelDisplayName) }
                      : undefined
                  }
                >
                  {avatarPreviewUrl || channel.imageRef ? (
                    <img
                      src={avatarPreviewUrl ?? channel.imageRef!}
                      alt={channelDisplayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    '#'
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold">{channelDisplayName}</p>
                  {channel.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{channel.description}</p>
                  )}
                </div>
              </div>
            )}

            {isAdmin && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="channelName">Name</Label>
                  <Input
                    id="channelName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Group name"
                    maxLength={80}
                    className="resize-none w-full text-left rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none leading-relaxed rounded-[10px] transition-all duration-150 shadow-social-card"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="channelDescription">Description</Label>
                  <Textarea
                    id="channelDescription"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What's this group about?"
                    maxLength={280}
                    rows={3}
                    className="resize-none w-full text-left rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none leading-relaxed rounded-[10px] transition-all duration-150 shadow-social-card"
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {description.length}/280
                  </p>
                </div>

                {/* Privacy */}
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium">Visibility</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Private channels require an invite link to join.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    {(['public', 'private'] as const).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setIsPrivate(type === 'private')}
                        className={cn(
                          'flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                          (type === 'private') === isPrivate
                            ? 'bg-primary/10 border border-primary/30'
                            : 'border border-transparent hover:bg-muted',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center',
                            (type === 'private') === isPrivate
                              ? 'border-primary bg-primary'
                              : 'border-muted-foreground',
                          )}
                        >
                          {(type === 'private') === isPrivate && (
                            <span className="w-1.5 h-1.5 rounded-full bg-white" />
                          )}
                        </span>
                        <div>
                          <p className="text-sm font-medium capitalize">{type}</p>
                          <p className="text-xs text-muted-foreground">
                            {type === 'public'
                              ? 'Anyone can discover and join this group'
                              : 'Only people with an invite link can join'}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Invite link (shown when private or when an invite code exists) */}
                {isPrivate && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium flex items-center gap-1.5">
                      <Link2 className="w-4 h-4" />
                      Invite Link
                    </p>
                    {inviteCode ? (
                      <div className="flex items-center gap-2">
                        <Input
                          readOnly
                          value={inviteCode}
                          className="text-xs font-mono bg-muted border-border"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCopyInvite}
                          className="flex-shrink-0 gap-1.5"
                        >
                          {inviteCopied ? (
                            <Check className="w-3.5 h-3.5" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                          {inviteCopied ? 'Copied' : 'Copy'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleGenerateInvite}
                          disabled={inviteGenerating}
                          title="Regenerate invite code"
                          className="flex-shrink-0"
                        >
                          <RefreshCw
                            className={cn('w-3.5 h-3.5', inviteGenerating && 'animate-spin')}
                          />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateInvite}
                        disabled={inviteGenerating}
                        className="gap-1.5"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        Generate invite link
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Mute for non-admin view */}
            {!isAdmin && (
              <div className="space-y-0 divide-y divide-border/50">
                <SettingRow
                  label="Mute Channel"
                  description="Suppress notifications from this channel"
                >
                  <Switch
                    checked={isMuted}
                    onCheckedChange={handleToggleMute}
                    disabled={muteLoading}
                  />
                </SettingRow>
              </div>
            )}
          </div>
        );
      }

      case 'Members': {
        const sortedMembers = [...members].sort((a, b) => {
          const order = { owner: 0, admin: 1, moderator: 2, member: 3 };
          return (
            (order[a.role as keyof typeof order] ?? 4) - (order[b.role as keyof typeof order] ?? 4)
          );
        });

        const adminMembers = sortedMembers.filter((m) => m.role === 'owner' || m.role === 'admin');
        const memberIds = new Set(members.map((m) => m.userId));

        return (
          <div className="space-y-6">
            {/* Add member (admin only) */}
            {isAdmin && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm font-semibold">Add Member</p>
                </div>
                <AddMemberSearch
                  channelId={channel.channelId}
                  memberIds={memberIds}
                  onAdded={invalidateChannel}
                />
              </div>
            )}

            {/* Full member list */}
            <div className="space-y-2">
              <p className="text-sm font-semibold">
                Members{' '}
                <span className="text-muted-foreground font-normal">({members.length})</span>
              </p>
              <div className="divide-y divide-border/50">
                {sortedMembers.map((m) => (
                  <MemberRow
                    key={m.userId}
                    member={m}
                    displayName={m.displayName ?? m.userId}
                    avatarUrl={memberAvatarUrls[m.userId]}
                    currentUserId={currentUserId}
                    currentUserRole={currentMember?.role ?? 'member'}
                    channelId={channel.channelId}
                    onRoleChange={handleRoleChange}
                    onRemove={handleRemoveMember}
                    onBan={handleBanMember}
                    onPermissionEdit={openMemberPermEdit}
                  />
                ))}
              </div>
            </div>

            {/* Admins section (admin/owner only) */}
            {isAdmin && adminMembers.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  <p className="text-sm font-semibold">Administrators</p>
                </div>
                <div className="rounded-lg border border-border px-3 divide-y divide-border/50">
                  {adminMembers.map((m) => (
                    <AdminRow
                      key={m.userId}
                      member={m}
                      displayName={m.displayName ?? m.userId}
                      avatarUrl={memberAvatarUrls[m.userId]}
                      currentUserId={currentUserId}
                      currentUserRole={currentMember?.role ?? 'member'}
                      onPermissionEdit={openMemberPermEdit}
                    />
                  ))}
                </div>
                {isOwner && (
                  <p className="text-xs text-muted-foreground px-0.5">
                    Promote a member using the ⋮ menu above, then edit their admin permissions here.
                  </p>
                )}
              </div>
            )}

            {/* Banned users */}
            {isAdmin && bans.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-destructive">Banned Users</p>
                <div className="rounded-lg border border-border px-3 divide-y divide-border/50">
                  {bans.map((ban) => (
                    <div key={ban.banId} className="flex items-center justify-between py-2.5 gap-3">
                      <div>
                        <p className="text-sm font-medium">{ban.bannedUserId}</p>
                        {ban.reason && (
                          <p className="text-xs text-muted-foreground">{ban.reason}</p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnban(ban.bannedUserId)}
                        className="flex-shrink-0 text-xs"
                      >
                        Unban
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'Permissions': {
        return (
          <div className="space-y-8">
            <SectionHeading
              title="Permissions"
              description="Default permissions apply to all members. Individual exceptions override these defaults."
            />

            <PermissionToggles
              title="Channel Actions"
              description="What all members can do by default."
              rows={PERMISSION_ROWS}
              permissions={defaultPermissions}
              onChange={setDefaultPermissions}
            />

            <div className="space-y-0 divide-y divide-border/50">
              <SettingRow
                label="Mute Channel"
                description="Suppress notifications from this channel"
              >
                <Switch
                  checked={isMuted}
                  onCheckedChange={handleToggleMute}
                  disabled={muteLoading}
                />
              </SettingRow>
            </div>
          </div>
        );
      }

      case 'Privacy': {
        if (!isDirect) return null;

        return (
          <div className="space-y-6">
            <SectionHeading title="Privacy" description="Manage this conversation." />
            <div className="space-y-0 divide-y divide-border/50">
              <SettingRow
                label="Mute Conversation"
                description="Suppress notifications from this DM"
              >
                <Switch
                  checked={isMuted}
                  onCheckedChange={handleToggleMute}
                  disabled={muteLoading}
                />
              </SettingRow>
            </div>
            {partnerProfile && (
              <div className="rounded-lg border border-border p-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Block User</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {partnerProfile.displayName || partnerProfile.username} will not be able to send
                    you messages.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBlockPartner}
                  disabled={blockLoading}
                  className="flex-shrink-0 gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                >
                  Block
                </Button>
              </div>
            )}
            {!showDmDeleteConfirm ? (
              <div className="rounded-lg border border-border p-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Remove Conversation</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Hides this chat from your list. The other participant still keeps the
                    conversation and its history.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDmDeleteConfirm(true)}
                  className="flex-shrink-0 gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </Button>
              </div>
            ) : (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 space-y-3">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive">
                      Remove this conversation?
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      This chat will be hidden from your list. The other participant keeps the full
                      conversation and history. You can start a new chat with them any time.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleLeave}
                    disabled={leaveLoading}
                    className="gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {leaveLoading ? 'Removing…' : 'Yes, remove'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowDmDeleteConfirm(false)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 'Danger': {
        const otherMembers = members.filter((m) => m.userId !== currentUserId);

        // Transfer ownership panel
        if (dangerMode === 'transfer-leave') {
          return (
            <div className="space-y-6">
              <div className="flex flex-row-reverse items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setDangerMode(null);
                    setSelectedNewOwnerId(null);
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Back
                </button>
                <div>
                  <h2 className="text-base font-semibold">Transfer Ownership</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Select a member to become the new owner before you leave.
                  </p>
                </div>
              </div>

              <div className="space-y-1 divide-y divide-border/50 rounded-lg border border-border overflow-hidden">
                {otherMembers
                  .sort((a, b) => {
                    const order = { admin: 0, moderator: 1, member: 2 };
                    return (
                      (order[a.role as keyof typeof order] ?? 3) -
                      (order[b.role as keyof typeof order] ?? 3)
                    );
                  })
                  .map((m) => (
                    <button
                      key={m.userId}
                      type="button"
                      onClick={() => setSelectedNewOwnerId(m.userId)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors',
                        selectedNewOwnerId === m.userId ? 'bg-primary/10' : 'hover:bg-muted',
                      )}
                    >
                      <MemberAvatar
                        displayName={m.displayName ?? m.userId}
                        avatarUrl={memberAvatarUrls[m.userId]}
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{m.displayName ?? m.userId}</p>
                      </div>
                      <RoleBadge role={m.role} />
                      {selectedNewOwnerId === m.userId && (
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleTransferAndLeave}
                  disabled={!selectedNewOwnerId || transferLeaveLoading}
                  className="gap-1.5"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  {transferLeaveLoading ? 'Leaving…' : 'Transfer & Leave'}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setDangerMode(null);
                    setSelectedNewOwnerId(null);
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        }

        // Delete confirmation panel
        if (dangerMode === 'delete-confirm') {
          return (
            <div className="space-y-6">
              <div className="flex flex-row-reverse items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setDangerMode(null)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Back
                </button>
                <div>
                  <h2 className="text-base font-semibold text-destructive">Delete Channel</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    This action is permanent and cannot be undone.
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 space-y-2">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-destructive">
                      You are about to permanently delete &ldquo;{channelDisplayName}&rdquo;
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-0.5 list-disc list-inside">
                      <li>All messages and media will be deleted</li>
                      <li>
                        All {members.length} member{members.length !== 1 ? 's' : ''} will lose
                        access
                      </li>
                      <li>This cannot be recovered</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                  className="gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {deleteLoading ? 'Deleting…' : 'Yes, delete permanently'}
                </Button>
                <button
                  type="button"
                  onClick={() => setDangerMode(null)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        }

        // Default danger zone
        return (
          <div className="space-y-6">
            <SectionHeading title="Danger Zone" description="These actions cannot be undone." />
            <div className="space-y-3">
              <div className="rounded-lg border border-border p-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">
                    {channel.hasLeft ? 'Rejoin Channel' : 'Leave Channel'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {channel.hasLeft
                      ? 'You left this channel. Rejoin to send messages again.'
                      : isOwner
                        ? otherMembers.length === 0
                          ? 'You are the only member. Delete the channel instead.'
                          : 'As owner, you must transfer ownership before leaving.'
                        : 'You can still view channel history after leaving.'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (channel.hasLeft) {
                      handleRejoin();
                    } else if (isOwner) {
                      if (otherMembers.length > 0) {
                        setSelectedNewOwnerId(null);
                        setDangerMode('transfer-leave');
                      }
                    } else {
                      handleLeave();
                    }
                  }}
                  disabled={
                    leaveLoading || (!channel.hasLeft && isOwner && otherMembers.length === 0)
                  }
                  className="flex-shrink-0 gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  {leaveLoading
                    ? channel.hasLeft
                      ? 'Rejoining…'
                      : 'Leaving…'
                    : channel.hasLeft
                      ? 'Rejoin'
                      : 'Leave'}
                </Button>
              </div>
              {isOwner && (
                <div className="rounded-lg border border-destructive/30 p-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-destructive">Delete Channel</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Permanently delete this channel and all its messages.
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDangerMode('delete-confirm')}
                    disabled={deleteLoading}
                    className="flex-shrink-0 gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      }
    }
  }

  // Determine whether to show a floating save bar
  const showInfoSaveBar =
    !editingMember && activeTab === 'Info' && hasInfoChanges && isAdmin && !isDirect;
  const showPermSaveBar = !editingMember && activeTab === 'Permissions' && hasPermChanges;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="p-0 gap-0 flex flex-col overflow-hidden w-full max-w-full h-screen rounded-none translate-x-0 translate-y-0 top-0 left-0 border-0 sm:max-w-2xl sm:h-[85vh] sm:max-h-[720px] sm:rounded-xl sm:border sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]"
      >
        <DialogTitle className="sr-only">Channel Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Manage this channel&apos;s settings, members, and permissions.
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-7 h-7 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center text-xs font-bold text-white"
              style={{
                background:
                  channel.avatarFileId || channel.imageRef
                    ? undefined
                    : seededGradient(channelDisplayName),
              }}
            >
              {channel.imageRef && !channel.avatarFileId ? (
                <img src={channel.imageRef} alt="" className="w-full h-full object-cover" />
              ) : avatarPreviewUrl ? (
                <img src={avatarPreviewUrl} alt="" className="w-full h-full object-cover" />
              ) : isDirect ? (
                channelDisplayName.charAt(0).toUpperCase()
              ) : (
                '#'
              )}
            </div>
            <span className="text-base font-semibold truncate">{channelDisplayName}</span>
          </div>
          <DialogClose asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full flex-shrink-0">
              <X className="h-4 w-4" />
            </Button>
          </DialogClose>
        </div>

        {/* Mobile: horizontal tab bar */}
        <div className="md:hidden flex overflow-x-auto gap-1 px-3 py-2 border-b border-border flex-shrink-0 scrollbar-none">
          {tabs.map((tab) => (
            <TabItem
              key={tab.name}
              tab={tab}
              active={activeTab === tab.name}
              onClick={() => {
                setEditingMember(null);
                setDangerMode(null);
                setSelectedNewOwnerId(null);
                setActiveTab(tab.name);
              }}
              mobile
            />
          ))}
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden relative">
          {/* Desktop sidebar */}
          <aside className="hidden md:flex flex-col w-48 flex-shrink-0 border-r border-border p-3 gap-0.5 overflow-y-auto">
            {tabs.map((tab) => (
              <TabItem
                key={tab.name}
                tab={tab}
                active={activeTab === tab.name}
                onClick={() => {
                  setEditingMember(null);
                  setActiveTab(tab.name);
                }}
              />
            ))}
          </aside>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-5 pb-24">{renderContent()}</div>

          {/* Floating save bar — Info changes */}
          {showInfoSaveBar && (
            <div className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-4 px-5 py-3 bg-background/95 backdrop-blur-sm border-t border-border shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
              <p className="text-sm text-muted-foreground">
                Careful — your changes have not yet been saved.
              </p>
              <div className="flex items-center gap-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleInfoReset}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Reset
                </button>
                <Button
                  size="sm"
                  onClick={handleInfoSave}
                  disabled={saving}
                  className="cursor-pointer bg-foreground p-1 px-2 text-[10px] text-background rounded-md h-fit hover:bg-foreground/80 flex items-center gap-2"
                >
                  <Save className="h-3.5 w-3.5" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}

          {/* Floating save bar — Permissions changes */}
          {showPermSaveBar && (
            <div className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-4 px-5 py-3 bg-background/95 backdrop-blur-sm border-t border-border shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
              <p className="text-sm text-muted-foreground">
                Careful — your changes have not yet been saved.
              </p>
              <div className="flex items-center gap-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setDefaultPermissions(channel.defaultPermissions ?? {})}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Reset
                </button>
                <Button
                  size="sm"
                  onClick={handlePermSave}
                  disabled={permSaving}
                  className="cursor-pointer bg-foreground p-1 px-2 text-[10px] text-background rounded-md h-fit hover:bg-foreground/80 flex items-center gap-2"
                >
                  <Save className="h-3.5 w-3.5" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
